<template>
    <div class="container">
        <div class="row mt-2">
            <div class="col-6">
                <input type="button" class="btn btn-success" :value="$t('save_changes')" @click="saveChanges"
                    :disabled="editedDevices.length == 0" />
            </div>
        </div>
        <div v-if="message" :class="alertClass">{{ message }}</div>
        <div class="row mt-3 w-75">
            <div>
                <input id="filter_devices" class="form-control" type="text" v-model="searchQuery"
                    :placeholder="$t('search') + '...'" />
            </div>
            <table class="table">
                <thead>
                    <tr>
                        <th v-for="(header) in dataHeaders" :key="header" @click="sortTable(header)">
                            {{ $t(`dataheaders.${header}`) }}
                            <span v-if="sortKey === header">
                                <i v-if="sortOrder === 'asc'" class="fas fa-sort-up" style="color: blue;"></i>
                                <i v-else class="fas fa-sort-down" style="color: blue;"></i>
                            </span>
                            <i v-else class="fas fa-sort" style="color: grey;"></i>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="device in filteredAndSortedData" :key="device.device_id" :class="editedClass(device)">
                        <td>{{ device.device_name }}</td>
                        <td :class="missingGroup(device.group_id)">
                            <select v-model="device.group_id" @change="changesMade(device)">
                                <option :value="null">{{ $t('missing_group') }}</option>
                                <option v-for="group in groups" :key="group.group_id" :value="group.group_id">
                                    {{ group.group_name }}
                                </option>
                            </select>
                        </td>
                        <td>{{ getDeviceAddress(device) }}</td>
                        <td><input type="text" v-model="device.device_nickname" @change="changesMade(device)"></td>
                    </tr>
                </tbody>
            </table>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
const { getCookie } = require('@/helpers');
import { fetchData } from '@/helpers';

export default {
    name: 'DeviceTools',
    data() {
        return {
            dataHeaders: ['device_name', 'group', 'address', 'device_nickname'],
            originalDevices: [],
            deviceGroups: [],
            editedDevices: [],
            sortKey: 'device_name',
            sortOrder: 'asc',
            searchQuery: '',
            message: '',
            alertClass: ''
        };
    },
    emits: ['data-updated'],
    props: {
        devices: {
            type: Array,
            default: () => []
        },
        groups: {
            type: Array,
            default: () => []
        },
    },
    computed: {
        filteredAndSortedData() {
            if (!this.devices) return [];
            let filteredData = this.devices.filter(item => {
                return Object.keys(item).some(key => {
                    return String(item[key]).toLowerCase().includes(this.searchQuery.toLowerCase());
                }) || this.groups.some(group => {
                    if (group.group_id === item.group_id) {
                        return String(group.address).toLowerCase().includes(this.searchQuery.toLowerCase());
                    }
                });
            });

            if (this.sortKey) {
                filteredData.sort((a, b) => {
                    let result = 0;
                    let aValue = a[this.sortKey];
                    let bValue = b[this.sortKey];
                    //console.log('aValue:', aValue, 'bValue:', bValue);

                    if (this.sortKey === 'group') {
                        aValue = this.getGroupName(a.group_id);
                        bValue = this.getGroupName(b.group_id);
                    } else if (this.sortKey === 'address') {
                        aValue = this.getDeviceAddress(a);
                        bValue = this.getDeviceAddress(b);
                    }

                    // Handle null or undefined values
                    if ((aValue === null || aValue === undefined) && (bValue !== null && bValue !== undefined)) {
                        return this.sortOrder === 'asc' ? 1 : -1; // `aValue` is null, so `bValue` should come first if ascending
                    }
                    if ((aValue !== null || aValue !== undefined) && (bValue === null || bValue === undefined)) {
                        return this.sortOrder === 'asc' ? -1 : 1; // `bValue` is null, so `aValue` should come first if ascending
                    }
                    if ((aValue === null || aValue === undefined) && (bValue === null || bValue === undefined)) {
                        return 0; // Both are null, so they are equal
                    }

                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        result = aValue.localeCompare(bValue);
                    } else {
                        if (aValue < bValue) result = -1;
                        if (aValue > bValue) result = 1;
                    }
                    return this.sortOrder === 'asc' ? result : -result;
                });
            }
            return filteredData;
        },
    },
    watch: {
        devices: {
            immediate: true,
            handler(newDevices) {
                if (newDevices.length > 0) {
                    this.initialize();
                }
            }
        }
    },
    methods: {
        fetchData,
        async initialize() {
            this.deviceGroups = await this.fetchData('devicegroups');
            await this.setDeviceGroups();
        },
        getGroupName(group_id) {
            const group = this.groups.find(g => g.group_id === group_id);
            return group ? group.group_name : '';
        },
        setMessage(message, isSuccess) {
            this.message = message;
            this.alertClass = isSuccess ? 'alert alert-success' : 'alert alert-warning';
        },
        getDeviceAddress(device) {
            const group = this.groups.find(g => g.group_id === device.group_id);
            return group ? group.address : '';
        },
        changesMade(device) {
            const originalDevice = this.originalDevices.find(d => d.device_id === device.device_id);
            const isEdited = JSON.stringify(device) !== JSON.stringify(originalDevice);
            const alreadyEdited = this.editedDevices.some(d => d.device_id === device.device_id);

            if (isEdited && !alreadyEdited) {
                this.editedDevices.push(device);
            } else if (!isEdited && alreadyEdited) {
                this.editedDevices = this.editedDevices.filter(d => d.device_id !== device.device_id);
            }
            return isEdited;
        },
        editedClass(device) {
            //console.log(this.editedDevices.some(d => d.device_id === device.device_id));
            if (this.editedDevices.some(d => d.device_id === device.device_id)) {
                return 'table-warning';
            } else {
                return '';
            }
        },
        missingGroup(group_id) {
            return group_id ? '' : 'table-danger';
        },
        async setDeviceGroups() {
            this.devices.forEach(device => {
                const deviceGroup = this.deviceGroups.find(dg => dg.device_id === device.device_id);
                if (deviceGroup) {
                    device.group_id = deviceGroup.group_id;
                } else {
                    device.group_id = null;
                }
            });
            //console.log('Devices:', this.devices);
            this.originalDevices = JSON.parse(JSON.stringify(this.devices));
        },
        sortTable(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortKey = key;
                this.sortOrder = 'asc';
            }
        },
        async saveChanges() {
            this.message = '';
            //console.log('Edited Devices:', this.editedDevices);
            if (this.editedDevices.length > 0) {
                let errorMessages = [];

                this.editedDevices.forEach(device => {
                    const originalDevice = this.originalDevices.find(d => d.device_id === device.device_id);
                    //console.log('Original Device:', originalDevice);

                    if (device.group_id !== originalDevice.group_id) {
                        axios.post(`${this.$url}/devicegroups`, {
                            data: [{
                                device_id: device.device_id,
                                group_id: device.group_id
                            }],
                            action: 'add'
                        }, {
                            headers: {
                                'Authorization': 'Bearer ' + getCookie('token'),
                                'Content-Type': 'application/json'
                            }
                        }).then(() => {
                            this.editedDevices = this.editedDevices.filter(d => d.device_id !== device.device_id);
                        }).catch(error => {
                            if (error.response) {
                                // Server responded with a status other than 2xx
                                console.error('Error response:', error.response);
                                errorMessages.push(`Device name ${device.device_name}: ${error.response.data}`);
                            } else if (error.request) {
                                // Request was made but no response was received
                                console.error('Error request:', error.request);
                                errorMessages.push(`Device name ${device.device_name}: No response received from server.`);
                            } else {
                                // Something else happened while setting up the request
                                console.error('Error message:', error.message);
                                errorMessages.push(`Device name ${device.device_name}: ${error.message}`);
                            }
                        });
                    }

                    if (device.device_nickname !== originalDevice.device_nickname) {
                        axios.post(`${this.$url}/devices`, {
                            device_id: device.device_id,
                            device_nickname: device.device_nickname,
                            action: 'update'
                        }, {
                            headers: {
                                Authorization: 'Bearer ' + getCookie('token'),
                                'Content-Type': 'application/json'
                            }
                        }).then(() => {
                            this.editedDevices = this.editedDevices.filter(d => d.device_id !== device.device_id);
                        }).catch(error => {
                            if (error.response) {
                                // Server responded with a status other than 2xx
                                console.error('Error response:', error.response);
                                errorMessages.push(`Device name ${device.device_name}: ${error.response.data}`);
                            } else if (error.request) {
                                // Request was made but no response was received
                                console.error('Error request:', error.request);
                                errorMessages.push(`Device name ${device.device_name}: No response received from server.`);
                            } else {
                                // Something else happened while setting up the request
                                console.error('Error message:', error.message);
                                errorMessages.push(`Device name ${device.device_name}: ${error.message}`);
                            }
                        });
                    }
                });
                if (errorMessages.length > 0) {
                    this.setMessage(this.$t('error') + ': ' + errorMessages.join(', '), false);
                } else {
                    this.setMessage(this.$t('success'), true);
                    this.editedDevices = [];
                    this.$emit('data-updated');
                    setTimeout(() => {
                        this.setMessage('', true);
                    }, 3000);

                }
            }
        }
    }
}
</script>

<style scoped></style>